import type { SyntheticEvent } from 'react';
import type { TempFileMetadata } from '@cnapse/shared/temp-file-schemas.js';
import { resolveFilePreviewUrl } from '../../utils/filePreview';

export function tempFileThumbnailUrl(file: TempFileMetadata): string | undefined {
  return file.thumbnailUrl ?? file.fullSizeUrl;
}

/** Full-resolution URL for lightbox / detail preview. */
export function tempFileFullPreviewUrl(file: TempFileMetadata): string | undefined {
  return file.fullSizeUrl ?? file.thumbnailUrl;
}

/** @deprecated Prefer tempFileThumbnailUrl for grids and tempFileFullPreviewUrl for lightbox. */
export function tempFilePreviewUrl(file: TempFileMetadata): string | undefined {
  return tempFileThumbnailUrl(file);
}

/** Fall back to full-size URL, then mint a fresh presigned URL from objectKey. */
export function handleTempFileImageError(
  file: TempFileMetadata,
  event: SyntheticEvent<HTMLImageElement>,
): void {
  const target = event.currentTarget;
  if (file.fullSizeUrl && target.src !== file.fullSizeUrl) {
    target.src = file.fullSizeUrl;
    return;
  }
  void resolveFilePreviewUrl({ objectKey: file.objectKey }).then((url) => {
    if (url && target.src !== url) {
      target.src = url;
    }
  });
}

export function tempFileSourceLabel(source: TempFileMetadata['metadata']['source']): string {
  switch (source) {
    case 'container_slot_scan':
      return 'Slot scan';
    case 'manual_upload':
      return 'Manual upload';
    default:
      return source;
  }
}

export function tempFileExpirationColor(daysUntil: number): 'error' | 'warning' | 'success' {
  if (daysUntil <= 3) return 'error';
  if (daysUntil <= 7) return 'warning';
  return 'success';
}

export function formatTempFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function isTempFileImage(file: TempFileMetadata): boolean {
  return file.mimeType.startsWith('image/');
}

export type TempFileMediaKind = 'image' | 'document' | 'any';

export function tempFileMatchesMediaKind(
  file: TempFileMetadata,
  mediaKind: TempFileMediaKind,
): boolean {
  if (mediaKind === 'any') return true;
  const isImage = isTempFileImage(file);
  return mediaKind === 'image' ? isImage : !isImage;
}
